import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText } from 'react-native-svg';
import Footer from '../components/Footer';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const colors = {
  primary: '#4F46E5',
  background: '#FFFFFF',
  surface: '#F8FAFC',
  cardBackground: '#FFFFFF',
  border: '#E2E8F0',
  text: '#1E293B',
  textSecondary: '#64748B',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

const EMICalculatorScreen = () => {
  const [loanAmount, setLoanAmount] = useState('1000000');
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTenure, setLoanTenure] = useState(5);
  const [tenureType, setTenureType] = useState('years');
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);
  const [displayedRows, setDisplayedRows] = useState(5);
  const [monthlyIncome, setMonthlyIncome] = useState('50000');
  const [showAffordability, setShowAffordability] = useState(false);

  const tenureOptions = [
    { label: 'Years', value: 'years' },
    { label: 'Months', value: 'months' },
  ];

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Helper function to remove formatting and get clean number
  const cleanNumber = (str) => {
    return str.replace(/,/g, '');
  };

  // Handle loan amount input with formatting
  const handleLoanAmountChange = (text) => {
    const cleanText = cleanNumber(text);
    if (cleanText === '' || /^\d+$/.test(cleanText)) {
      setLoanAmount(cleanText);
    }
  };

  const calculateEMI = () => {
    const principal = parseFloat(loanAmount);
    const rate = parseFloat(interestRate);
    const tenure = parseFloat(loanTenure);

    // Enhanced input validation
    if (isNaN(principal) || principal <= 0) {
      setResults(null);
      return;
    }
    
    if (isNaN(rate) || rate <= 0 || rate > 50) {
      setResults(null);
      return;
    }
    
    if (isNaN(tenure) || tenure <= 0) {
      setResults(null);
      return;
    }

    // Convert tenure to months if in years
    const tenureInMonths = tenureType === 'years' ? tenure * 12 : tenure;
    
    // Validate tenure limits
    if (tenureInMonths > 360) { // Max 30 years
      setResults(null);
      return;
    }
    
    // Monthly interest rate
    const monthlyRate = rate / (12 * 100);
    
    // EMI calculation using formula: P * r * (1+r)^n / ((1+r)^n - 1)
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, tenureInMonths)) / 
                (Math.pow(1 + monthlyRate, tenureInMonths) - 1);
    
    const totalAmount = emi * tenureInMonths;
    const totalInterest = totalAmount - principal;

    // Generate year-wise breakdown for amortization schedule
    const breakdown = [];
    let remainingPrincipal = principal;
    
    for (let year = 1; year <= Math.ceil(tenureInMonths / 12); year++) {
      const startMonth = (year - 1) * 12 + 1;
      const endMonth = Math.min(year * 12, tenureInMonths);
      let yearlyInterest = 0;
      let yearlyPrincipal = 0;
      
      for (let month = startMonth; month <= endMonth; month++) {
        const monthlyInterest = remainingPrincipal * monthlyRate;
        const monthlyPrincipal = emi - monthlyInterest;
        
        yearlyInterest += monthlyInterest;
        yearlyPrincipal += monthlyPrincipal;
        remainingPrincipal -= monthlyPrincipal;
      }
      
      breakdown.push({
        year,
        principal: Math.round(yearlyPrincipal),
        interest: Math.round(yearlyInterest),
        balance: Math.max(0, Math.round(remainingPrincipal)),
      });
    }

    setResults({
      emi: Math.round(emi),
      totalInterest: Math.round(totalInterest),
      totalAmount: Math.round(totalAmount),
      principal: Math.round(principal),
      breakdown,
      tenureInMonths,
    });
  };

  useEffect(() => {
    calculateEMI();
  }, [loanAmount, interestRate, loanTenure, tenureType]);

  const loadMoreRows = () => {
    setDisplayedRows(prev => prev + 5);
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <View style={styles.headerContent}>
        <Text style={styles.headerTitle}>EMI Calculator</Text>
          <Text style={styles.headerSubtitle}>
            Calculate your loan EMI, total interest, and payment schedule
          </Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <Text style={styles.sectionTitle}>Loan Details</Text>
      
      {/* Loan Amount Input */}
      <View style={styles.inputGroup}>
        <Text style={styles.inputLabel}>Loan amount</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={formatNumber(loanAmount)}
            onChangeText={handleLoanAmountChange}
            placeholder="10,00,000"
            keyboardType="numeric"
            placeholderTextColor={colors.textSecondary}
          />
        </View>
      </View>

      {/* Preset Loan Amount Buttons */}
      <View style={styles.presetContainer}>
        <Text style={styles.presetLabel}>Quick Select:</Text>
        <View style={styles.presetButtons}>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('500000')}
          >
            <Text style={styles.presetButtonText}>₹5L</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('1000000')}
          >
            <Text style={styles.presetButtonText}>₹10L</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('2500000')}
          >
            <Text style={styles.presetButtonText}>₹25L</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.presetButton} 
            onPress={() => setLoanAmount('5000000')}
          >
            <Text style={styles.presetButtonText}>₹50L</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Interest Rate Slider */}
      <View style={styles.inputGroup}>
        <View style={styles.sliderHeader}>
          <Text style={styles.inputLabel}>Rate of interest (p.a)</Text>
          <View style={styles.valueContainer}>
            <Text style={styles.valueText}>{interestRate.toFixed(1)}%</Text>
          </View>
        </View>
        <Slider
          style={styles.slider}
          minimumValue={1}
          maximumValue={20}
          value={interestRate}
          onValueChange={setInterestRate}
          minimumTrackTintColor={colors.success}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
      </View>

      {/* Loan Tenure Slider */}
      <View style={styles.inputGroup}>
        <View style={styles.sliderHeader}>
          <Text style={styles.inputLabel}>Loan tenure</Text>
          <View style={styles.valueContainer}>
            <Text style={styles.valueText}>{loanTenure} {tenureType === 'years' ? 'Yr' : 'Mo'}</Text>
          </View>
        </View>
        <Slider
          style={styles.slider}
          minimumValue={1}
          maximumValue={tenureType === 'years' ? 30 : 360}
          value={loanTenure}
          onValueChange={setLoanTenure}
          minimumTrackTintColor={colors.success}
          maximumTrackTintColor={colors.border}
          thumbStyle={styles.sliderThumb}
        />
      </View>

      {/* Affordability Calculator */}
      <View style={styles.affordabilityContainer}>
        <TouchableOpacity 
          style={styles.affordabilityHeader}
          onPress={() => setShowAffordability(!showAffordability)}
        >
          <Text style={styles.affordabilityTitle}>Loan Affordability Check</Text>
          <Ionicons 
            name={showAffordability ? "chevron-up" : "chevron-down"} 
            size={20} 
            color={colors.textSecondary} 
          />
        </TouchableOpacity>
        
        {showAffordability && (
          <View style={styles.affordabilityContent}>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Monthly Income</Text>
              <View style={styles.inputContainer}>
                <Text style={styles.currencySymbol}>₹</Text>
                <TextInput
                  style={styles.textInput}
                  value={formatNumber(monthlyIncome)}
                  onChangeText={(text) => {
                    const cleanText = cleanNumber(text);
                    if (cleanText === '' || /^\d+$/.test(cleanText)) {
                      setMonthlyIncome(cleanText);
                    }
                  }}
                  placeholder="50,000"
                  keyboardType="numeric"
                  placeholderTextColor={colors.textSecondary}
                />
              </View>
            </View>
            
            {results && monthlyIncome && (
              <View style={styles.affordabilityResults}>
                <View style={styles.affordabilityItem}>
                  <Text style={styles.affordabilityLabel}>EMI to Income Ratio</Text>
                  <Text style={[
                    styles.affordabilityValue,
                    { color: (results.emi / parseFloat(monthlyIncome)) * 100 > 40 ? colors.error : colors.success }
                  ]}>
                    {((results.emi / parseFloat(monthlyIncome)) * 100).toFixed(1)}%
                  </Text>
                </View>
                <Text style={styles.affordabilityNote}>
                  {(results.emi / parseFloat(monthlyIncome)) * 100 > 40 
                    ? "⚠️ EMI exceeds recommended 40% of income" 
                    : "✅ EMI is within recommended limits"}
                </Text>
              </View>
            )}
          </View>
        )}
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const radius = 80;
    const strokeWidth = 20;
    const normalizedRadius = radius - strokeWidth * 2;
    const circumference = normalizedRadius * 2 * Math.PI;
    
    const principalPercentage = (results.principal / results.totalAmount) * 100;
    const interestPercentage = (results.totalInterest / results.totalAmount) * 100;
    
    const principalStrokeDasharray = `${(principalPercentage / 100) * circumference} ${circumference}`;
    const interestStrokeDasharray = `${(interestPercentage / 100) * circumference} ${circumference}`;
    const interestStrokeDashoffset = -((principalPercentage / 100) * circumference);

    return (
      <View style={styles.chartContainer}>
        <Svg height={radius * 2} width={radius * 2}>
          <Circle
            stroke={colors.border}
            fill="transparent"
            strokeWidth={strokeWidth}
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          <Circle
            stroke={colors.primary}
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={principalStrokeDasharray}
            strokeDashoffset={0}
            strokeLinecap="round"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
          <Circle
            stroke={colors.success}
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={interestStrokeDasharray}
            strokeDashoffset={interestStrokeDashoffset}
            strokeLinecap="round"
            r={normalizedRadius}
            cx={radius}
            cy={radius}
          />
        </Svg>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.primary }]} />
            <Text style={styles.legendText}>Loan</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Interest</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    return (
      <View style={styles.resultsContainer}>
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Monthly EMI</Text>
          <Text style={styles.resultValue}>₹{results.emi.toLocaleString('en-IN')}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Principal amount</Text>
          <Text style={styles.resultValue}>₹{results.principal.toLocaleString('en-IN')}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total interest</Text>
          <Text style={styles.resultValue}>₹{results.totalInterest.toLocaleString('en-IN')}</Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Total amount</Text>
          <Text style={styles.resultValue}>₹{results.totalAmount.toLocaleString('en-IN')}</Text>
        </View>
      </View>
    );
  };

  const renderAmortizationSchedule = () => {
    if (!results || !results.breakdown) return null;

    return (
      <View style={styles.scheduleContainer}>
        <Text style={styles.scheduleTitle}>Yearly repayment schedule</Text>
        
        <View style={styles.scheduleTable}>
          <View style={styles.tableHeader}>
            <Text style={styles.tableHeaderText}>Year</Text>
            <Text style={styles.tableHeaderText}>Beginning Balance</Text>
            <Text style={styles.tableHeaderText}>EMI</Text>
            <Text style={styles.tableHeaderText}>Principal</Text>
            <Text style={styles.tableHeaderText}>Interest</Text>
            <Text style={styles.tableHeaderText}>Ending Balance</Text>
          </View>
          
          {results.breakdown.slice(0, displayedRows).map((item, index) => (
            <View key={index} style={styles.tableRow}>
              <Text style={styles.tableCellText}>{item.year}</Text>
              <Text style={styles.tableCellText}>₹{(results.principal - (index > 0 ? results.breakdown.slice(0, index).reduce((sum, prev) => sum + prev.principal, 0) : 0)).toLocaleString('en-IN')}</Text>
              <Text style={styles.tableCellText}>₹{(results.emi * (item.year === Math.ceil(results.tenureInMonths / 12) ? results.tenureInMonths % 12 || 12 : 12)).toLocaleString('en-IN')}</Text>
              <Text style={styles.tableCellText}>₹{item.principal.toLocaleString('en-IN')}</Text>
              <Text style={styles.tableCellText}>₹{item.interest.toLocaleString('en-IN')}</Text>
              <Text style={styles.tableCellText}>₹{item.balance.toLocaleString('en-IN')}</Text>
            </View>
          ))}
        </View>
        
        {displayedRows < results.breakdown.length && (
          <TouchableOpacity style={styles.loadMoreButton} onPress={loadMoreRows}>
            <Text style={styles.loadMoreText}>Load More</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  };

  const renderEMIInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>What is EMI Calculator?</Text>
      <Text style={styles.infoText}>
        An EMI (Equated Monthly Installment) Calculator is a comprehensive financial tool that helps you calculate 
        the fixed monthly payment amount for any type of loan. It uses advanced mathematical formulas to determine 
        your monthly installment based on loan amount, interest rate, and tenure, providing accurate calculations 
        for informed financial planning and budgeting decisions.
      </Text>

      <Text style={styles.infoSubtitle}>Understanding EMI</Text>
      <Text style={styles.infoText}>
        EMI is a fixed payment amount made by a borrower to a lender at a specified date each calendar month. 
        It consists of both principal and interest components, where the interest portion is higher in initial 
        years and gradually decreases as the principal portion increases over time. This systematic repayment 
        structure helps borrowers plan their finances effectively.
      </Text>

      <Text style={styles.infoSubtitle}>Benefits of Using EMI Calculator</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Instant and accurate EMI calculations for any loan type</Text>
        <Text style={styles.bulletPoint}>• Compare different loan scenarios and choose the best option</Text>
        <Text style={styles.bulletPoint}>• Plan your monthly budget with precise EMI amounts</Text>
        <Text style={styles.bulletPoint}>• Understand total interest payable over loan tenure</Text>
        <Text style={styles.bulletPoint}>• Analyze amortization schedule for payment breakdown</Text>
        <Text style={styles.bulletPoint}>• Make informed decisions about loan amount and tenure</Text>
        <Text style={styles.bulletPoint}>• Assess loan affordability against your income</Text>
        <Text style={styles.bulletPoint}>• Save time compared to manual calculations</Text>
      </View>

      <Text style={styles.infoSubtitle}>EMI Calculation Formula</Text>
      <View style={styles.formulaBox}>
        <Text style={styles.formulaText}>EMI = P × r × (1+r)ⁿ / ((1+r)ⁿ - 1)</Text>
        <Text style={styles.formulaText}>Where:</Text>
        <Text style={styles.formulaText}>P = Principal loan amount (₹)</Text>
        <Text style={styles.formulaText}>r = Monthly interest rate (Annual rate ÷ 12 ÷ 100)</Text>
        <Text style={styles.formulaText}>n = Total number of monthly installments</Text>
        <Text style={styles.formulaText}>EMI = Equated Monthly Installment (₹)</Text>
      </View>

      <Text style={styles.infoSubtitle}>Types of Loans for EMI Calculation</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Home Loans - Typically 15-30 years tenure with competitive rates</Text>
        <Text style={styles.bulletPoint}>• Personal Loans - Usually 1-5 years with higher interest rates</Text>
        <Text style={styles.bulletPoint}>• Car Loans - Generally 3-7 years with moderate interest rates</Text>
        <Text style={styles.bulletPoint}>• Education Loans - Flexible tenure with moratorium period options</Text>
        <Text style={styles.bulletPoint}>• Business Loans - Customized tenure based on business requirements</Text>
        <Text style={styles.bulletPoint}>• Gold Loans - Short-term loans with gold as collateral</Text>
      </View>

      <Text style={styles.infoSubtitle}>How to Use EMI Calculator?</Text>
      <View style={styles.stepsList}>
        <Text style={styles.stepText}>1. Enter the desired loan amount you want to borrow</Text>
        <Text style={styles.stepText}>2. Input the annual interest rate offered by your lender</Text>
        <Text style={styles.stepText}>3. Select your preferred loan tenure (in years or months)</Text>
        <Text style={styles.stepText}>4. View your monthly EMI and total cost breakdown instantly</Text>
        <Text style={styles.stepText}>5. Analyze the detailed amortization schedule</Text>
        <Text style={styles.stepText}>6. Compare different scenarios by adjusting parameters</Text>
        <Text style={styles.stepText}>7. Check loan affordability against your monthly income</Text>
      </View>

      <Text style={styles.infoSubtitle}>Factors Affecting EMI Amount</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Principal Amount - Higher loan amount increases EMI</Text>
        <Text style={styles.bulletPoint}>• Interest Rate - Higher rates result in higher EMIs</Text>
        <Text style={styles.bulletPoint}>• Loan Tenure - Longer tenure reduces EMI but increases total interest</Text>
        <Text style={styles.bulletPoint}>• Processing Fees - Additional charges affect total loan cost</Text>
        <Text style={styles.bulletPoint}>• Prepayment Options - Can reduce total interest burden</Text>
        <Text style={styles.bulletPoint}>• Credit Score - Better score may qualify for lower interest rates</Text>
      </View>

      <Text style={styles.infoSubtitle}>EMI Planning Best Practices</Text>
      <View style={styles.bulletList}>
        <Text style={styles.bulletPoint}>• Keep EMI within 40-50% of your monthly income</Text>
        <Text style={styles.bulletPoint}>• Compare offers from multiple lenders before deciding</Text>
        <Text style={styles.bulletPoint}>• Consider shorter tenure to save on total interest</Text>
        <Text style={styles.bulletPoint}>• Maintain emergency fund before taking loans</Text>
        <Text style={styles.bulletPoint}>• Read all terms and conditions carefully</Text>
        <Text style={styles.bulletPoint}>• Consider prepayment options for interest savings</Text>
        <Text style={styles.bulletPoint}>• Choose fixed vs floating rates based on market conditions</Text>
      </View>

      <View style={styles.tipBox}>
        <Text style={styles.tipTitle}>💡 Pro Tip</Text>
        <Text style={styles.tipText}>
          Use our EMI calculator to experiment with different loan amounts and tenures. 
          A slightly higher EMI with shorter tenure can save lakhs in interest payments!
        </Text>
      </View>

      <View style={styles.tipBox}>
        <Text style={styles.tipTitle}>📊 Smart Planning</Text>
        <Text style={styles.tipText}>
          Always factor in other monthly expenses and maintain a buffer. Your total EMIs 
          across all loans should not exceed 50% of your monthly income for financial stability.
        </Text>
      </View>

      <View style={styles.tipBox}>
        <Text style={styles.tipTitle}>🎯 Interest Optimization</Text>
        <Text style={styles.tipText}>
          Consider making prepayments when you have surplus funds. Even small prepayments 
          can significantly reduce your total interest burden and loan tenure.
        </Text>
      </View>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>Complete Guide to EMI Planning and Optimization</Text>
      
      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Understanding EMI Structure</Text>
        <Text style={styles.seoText}>
          Equated Monthly Installment (EMI) is a fixed payment amount made by a borrower to a lender 
          at a specified date each calendar month. EMIs consist of both principal and interest components, 
          with the interest portion being higher in the initial years and gradually decreasing over time.
        </Text>
        
        <View style={styles.featureGrid}>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>📊 Principal Component</Text>
            <Text style={styles.featureText}>The portion that reduces your outstanding loan balance</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>💰 Interest Component</Text>
            <Text style={styles.featureText}>The cost of borrowing, calculated on outstanding balance</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>📈 Amortization</Text>
            <Text style={styles.featureText}>Gradual shift from interest-heavy to principal-heavy payments</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>⏰ Fixed Schedule</Text>
            <Text style={styles.featureText}>Consistent monthly payments throughout loan tenure</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Key Calculator Features</Text>
        <Text style={styles.seoText}>
          Our advanced EMI calculator provides comprehensive loan analysis tools to help you make 
          informed financial decisions and optimize your borrowing strategy.
        </Text>
        
        <View style={styles.featureGrid}>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>🎯 Instant Calculations</Text>
            <Text style={styles.featureText}>Real-time EMI computation with dynamic updates</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>📋 Amortization Schedule</Text>
            <Text style={styles.featureText}>Detailed payment breakdown for entire loan tenure</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>💡 Affordability Analysis</Text>
            <Text style={styles.featureText}>Income-based loan eligibility assessment</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>📊 Visual Charts</Text>
            <Text style={styles.featureText}>Interactive pie charts showing cost breakdown</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Comparison with Other Loan Types</Text>
        <View style={styles.comparisonTable}>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonHeader}>Loan Type</Text>
            <Text style={styles.comparisonHeader}>Interest Rate</Text>
            <Text style={styles.comparisonHeader}>Tenure</Text>
            <Text style={styles.comparisonHeader}>Best For</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Personal Loan</Text>
            <Text style={styles.comparisonCell}>10-24%</Text>
            <Text style={styles.comparisonCell}>1-5 years</Text>
            <Text style={styles.comparisonCell}>Immediate needs</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Home Loan</Text>
            <Text style={styles.comparisonCell}>7-12%</Text>
            <Text style={styles.comparisonCell}>15-30 years</Text>
            <Text style={styles.comparisonCell}>Property purchase</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Car Loan</Text>
            <Text style={styles.comparisonCell}>8-15%</Text>
            <Text style={styles.comparisonCell}>3-7 years</Text>
            <Text style={styles.comparisonCell}>Vehicle purchase</Text>
          </View>
          <View style={styles.comparisonRow}>
            <Text style={styles.comparisonCell}>Business Loan</Text>
            <Text style={styles.comparisonCell}>12-20%</Text>
            <Text style={styles.comparisonCell}>1-10 years</Text>
            <Text style={styles.comparisonCell}>Business expansion</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Step-by-Step EMI Calculation</Text>
        <Text style={styles.seoText}>
          Understanding how EMI is calculated helps you make better financial decisions and 
          negotiate better terms with lenders.
        </Text>
        
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>1</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Enter Loan Amount (P)</Text>
              <Text style={styles.stepDescription}>The principal amount you wish to borrow</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>2</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Set Interest Rate (R)</Text>
              <Text style={styles.stepDescription}>Annual interest rate divided by 12 months</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>3</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Choose Tenure (N)</Text>
              <Text style={styles.stepDescription}>Loan duration in months</Text>
            </View>
          </View>
          <View style={styles.stepItem}>
            <Text style={styles.stepNumber}>4</Text>
            <View style={styles.stepContent}>
              <Text style={styles.stepTitle}>Apply EMI Formula</Text>
              <Text style={styles.stepDescription}>EMI = P × R × (1+R)^N / ((1+R)^N-1)</Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Smart EMI Optimization Strategies</Text>
        <Text style={styles.seoText}>
          Implement these proven strategies to minimize your total interest burden and 
          achieve financial freedom faster.
        </Text>
        
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>💰 Make Prepayments</Text>
            <Text style={styles.strategyText}>
              Use bonuses, tax refunds, or surplus income to make prepayments. Even small 
              prepayments can significantly reduce total interest and loan tenure.
            </Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>📊 Choose Shorter Tenure</Text>
            <Text style={styles.strategyText}>
              Opt for shorter loan tenure if you can afford higher EMIs. This reduces 
              total interest burden substantially over the loan period.
            </Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>🔄 Consider Balance Transfer</Text>
            <Text style={styles.strategyText}>
              If interest rates drop, consider transferring your loan to a lender 
              offering better rates to save on interest costs.
            </Text>
          </View>
          <View style={styles.strategyItem}>
            <Text style={styles.strategyTitle}>📈 Increase EMI Annually</Text>
            <Text style={styles.strategyText}>
              Step up your EMI by 5-10% annually as your income grows. This strategy 
              can reduce loan tenure by several years.
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Legal and Documentation Requirements</Text>
        <Text style={styles.seoText}>
          Understanding legal aspects and maintaining proper documentation is crucial for 
          smooth loan processing and future reference.
        </Text>
        
        <View style={styles.featureGrid}>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>📋 Income Proof</Text>
            <Text style={styles.featureText}>Salary slips, ITR, bank statements</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>🆔 Identity Verification</Text>
            <Text style={styles.featureText}>PAN card, Aadhaar, passport</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>🏠 Address Proof</Text>
            <Text style={styles.featureText}>Utility bills, rental agreement</Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>📊 Credit Report</Text>
            <Text style={styles.featureText}>CIBIL score and credit history</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Common EMI Mistakes to Avoid</Text>
        <Text style={styles.seoText}>
          Avoid these common pitfalls that can lead to financial stress and increased 
          borrowing costs over the loan tenure.
        </Text>
        
        <View style={styles.mistakesList}>
          <View style={styles.mistakeItem}>
            <Text style={styles.mistakeTitle}>❌ Ignoring Total Interest Cost</Text>
            <Text style={styles.mistakeText}>
              Don't focus only on EMI amount. Consider total interest payable over 
              the entire loan tenure when making decisions.
            </Text>
          </View>
          <View style={styles.mistakeItem}>
            <Text style={styles.mistakeTitle}>❌ Maxing Out Loan Eligibility</Text>
            <Text style={styles.mistakeText}>
              Borrowing the maximum eligible amount can strain your finances. 
              Keep EMI below 40% of your monthly income.
            </Text>
          </View>
          <View style={styles.mistakeItem}>
            <Text style={styles.mistakeTitle}>❌ Neglecting Emergency Fund</Text>
            <Text style={styles.mistakeText}>
              Maintain 6-12 months of expenses as emergency fund before taking 
              any loan to handle unexpected situations.
            </Text>
          </View>
          <View style={styles.mistakeItem}>
            <Text style={styles.mistakeTitle}>❌ Not Reading Fine Print</Text>
            <Text style={styles.mistakeText}>
              Understand all terms including prepayment charges, processing fees, 
              and penalty clauses before signing the agreement.
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSubtitle}>Success Stories and Case Studies</Text>
        <Text style={styles.seoText}>
          Learn from real-world examples of successful EMI management and loan optimization 
          strategies that helped borrowers save significantly.
        </Text>
        
        <View style={styles.featureGrid}>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>🎯 Prepayment Success</Text>
            <Text style={styles.featureText}>
              Rajesh saved ₹8 lakhs in interest by making annual prepayments of ₹2 lakhs 
              on his ₹50 lakh home loan, reducing tenure from 20 to 12 years.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>📊 Tenure Optimization</Text>
            <Text style={styles.featureText}>
              Priya chose 15-year tenure instead of 20 years for her ₹30 lakh loan, 
              paying ₹5,000 more EMI but saving ₹12 lakhs in total interest.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>🔄 Balance Transfer</Text>
            <Text style={styles.featureText}>
              Amit transferred his ₹40 lakh loan to a new lender offering 1% lower rate, 
              saving ₹6 lakhs over remaining 15-year tenure.
            </Text>
          </View>
          <View style={styles.featureItem}>
            <Text style={styles.featureTitle}>📈 Step-up Strategy</Text>
            <Text style={styles.featureText}>
              Neha increased her EMI by 10% annually, reducing her 25-year loan tenure 
              to 16 years and saving ₹15 lakhs in interest.
            </Text>
          </View>
        </View>
      </View>
    </View>
  );

  const renderFAQ = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity 
        style={styles.faqHeader}
        onPress={() => setShowFAQ(!showFAQ)}
      >
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons 
          name={showFAQ ? "chevron-up" : "chevron-down"} 
          size={24} 
          color={colors.textSecondary} 
        />
      </TouchableOpacity>
      
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is EMI and how does it work?</Text>
            <Text style={styles.faqAnswer}>
              EMI (Equated Monthly Installment) is a fixed payment amount made by a borrower to a lender at a specified date each calendar month. It consists of both principal and interest components. Initially, the interest portion is higher, but as you continue paying, the principal portion increases while the interest portion decreases.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How is EMI calculated?</Text>
            <Text style={styles.faqAnswer}>
              EMI is calculated using the formula: EMI = P × r × (1+r)^n / ((1+r)^n - 1), where P is the principal loan amount, r is the monthly interest rate (annual rate ÷ 12 ÷ 100), and n is the number of monthly installments. Our calculator uses this formula to provide instant and accurate results.
            </Text>
          </View>
          
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What factors affect my EMI amount?</Text>
            <Text style={styles.faqAnswer}>
              Three main factors affect EMI: loan amount (principal), interest rate, and loan tenure. Higher loan amount and interest rate increase EMI, while longer tenure decreases EMI but increases total interest. Your credit score, processing fees, and prepayment options also impact the overall loan cost.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What should be the ideal EMI to income ratio?</Text>
            <Text style={styles.faqAnswer}>
              Financial experts recommend keeping your total EMI obligations within 40-50% of your monthly income. This ensures you have sufficient funds for other expenses and emergencies. For home loans specifically, the ratio can go up to 60% given their longer tenure and tax benefits.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is this EMI calculator accurate?</Text>
            <Text style={styles.faqAnswer}>
              Yes, our EMI calculator uses the standard mathematical formula used by banks and financial institutions. It provides accurate calculations based on the inputs you provide. However, the actual EMI may vary slightly due to processing fees, insurance, and other charges that lenders may add.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Should I choose a longer or shorter loan tenure?</Text>
            <Text style={styles.faqAnswer}>
              Shorter tenure means higher EMI but lower total interest cost. Longer tenure means lower EMI but higher total interest. Choose based on your monthly cash flow and financial goals. If you can afford higher EMI, opt for shorter tenure to save on interest.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I prepay my loan to reduce EMI burden?</Text>
            <Text style={styles.faqAnswer}>
              Yes, most loans allow prepayment. You can either reduce your EMI amount or loan tenure through prepayment. Prepaying reduces the principal amount, which in turn reduces the interest burden. Check with your lender about prepayment charges and terms.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What is the difference between fixed and floating interest rates?</Text>
            <Text style={styles.faqAnswer}>
              Fixed interest rates remain constant throughout the loan tenure, providing EMI stability. Floating rates change with market conditions - they can increase or decrease, affecting your EMI. Fixed rates offer predictability, while floating rates may offer savings if rates decrease.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How does my credit score affect EMI?</Text>
            <Text style={styles.faqAnswer}>
              While credit score doesn't directly affect EMI calculation, it significantly impacts the interest rate offered by lenders. Higher credit scores (750+) typically qualify for lower interest rates, which results in lower EMIs. Maintain a good credit score for better loan terms.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What additional costs should I consider besides EMI?</Text>
            <Text style={styles.faqAnswer}>
              Besides EMI, consider processing fees (0.5-2% of loan amount), insurance premiums, legal charges, valuation fees, and prepayment charges. For home loans, also factor in registration costs, stamp duty, and maintenance charges. These can add 2-5% to your total loan cost.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can I get tax benefits on EMI payments?</Text>
            <Text style={styles.faqAnswer}>
              Tax benefits depend on the loan type. Home loan EMIs offer deductions under Section 80C (principal) and Section 24 (interest). Education loans provide interest deduction under Section 80E. Personal and car loans generally don't offer tax benefits. Consult a tax advisor for specific guidance.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>What happens if I miss an EMI payment?</Text>
            <Text style={styles.faqAnswer}>
              Missing EMI payments can result in late payment charges, negative impact on credit score, and potential legal action. Most lenders offer a grace period of 3-7 days. If you anticipate difficulty, contact your lender immediately to discuss restructuring options or temporary relief measures.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How can I reduce my EMI burden?</Text>
            <Text style={styles.faqAnswer}>
              You can reduce EMI burden by: making prepayments to reduce principal, extending loan tenure (increases total interest), refinancing at lower rates, or negotiating with your current lender for better terms. Balance transfer to another lender offering lower rates is also an option.
            </Text>
          </View>

          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is it better to take multiple small loans or one large loan?</Text>
            <Text style={styles.faqAnswer}>
              One large loan is generally better as it typically offers lower interest rates, single EMI management, and lower processing costs. However, consider your repayment capacity and the purpose of loans. Sometimes, staggered borrowing based on actual needs can be more prudent than borrowing a large amount upfront.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.mainContent}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      <View style={styles.fullWidthSection}>
        {renderAmortizationSchedule()}
        {renderEMIInformation()}
        {renderSEOContent()}
        {renderFAQ()}
      </View>
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background,
  },
  header: {
    backgroundColor: colors.primary,
    paddingHorizontal: 20,
    paddingVertical: 40,
    alignItems: 'center',
  },
  headerContent: {
    maxWidth: 800,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: 'bold',
    color: 'white',
    textAlign: 'center',
    marginBottom: 10,
  },
  headerSubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    lineHeight: 24,
  },
  content: {
    flexDirection: isMobile ? 'column' : 'row',
    maxWidth: 1200,
    alignSelf: 'center',
    width: '100%',
    padding: 20,
    gap: 30,
  },
  mainContent: {
    flexDirection: (isWeb && !isMobile) ? 'row' : 'column',
    minHeight: (isWeb && !isMobile) ? 600 : 'auto',
  },
  fullWidthSection: {
    flex: 1,
    alignSelf: 'stretch',
  },
  leftColumn: {
    flex: isMobile ? 1 : 0.6,
  },
  leftPanel: {
    flex: (isWeb && !isMobile) ? 1 : 1,
    backgroundColor: colors.background,
    padding: 20,
  },
  rightColumn: {
    flex: isMobile ? 1 : 0.4,
    alignItems: 'center',
  },
  rightPanel: {
    width: (isWeb && !isMobile) ? 400 : '100%',
    backgroundColor: colors.surface,
    borderLeftWidth: (isWeb && !isMobile) ? 1 : 0,
    borderLeftColor: colors.border,
    padding: 20,
  },
  calculatorContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 20,
  },
  inputGroup: {
    marginBottom: 24,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: colors.text,
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    paddingHorizontal: 12,
    backgroundColor: colors.surface,
  },
  currencySymbol: {
    fontSize: 16,
    color: colors.textSecondary,
    marginRight: 8,
  },
  textInput: {
    flex: 1,
    height: 48,
    fontSize: 16,
    color: colors.text,
  },
  presetContainer: {
    marginBottom: 16,
  },
  presetLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: colors.textSecondary,
    marginBottom: 8,
  },
  presetButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  presetButton: {
    flex: 1,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
    alignItems: 'center',
  },
  presetButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: colors.primary,
  },
  affordabilityContainer: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 20,
  },
  affordabilityHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  affordabilityTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  affordabilityContent: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  affordabilityResults: {
    backgroundColor: colors.background,
    borderRadius: 6,
    padding: 12,
    marginTop: 12,
  },
  affordabilityItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  affordabilityLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  affordabilityValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  affordabilityNote: {
    fontSize: 12,
    color: colors.textSecondary,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  sliderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  valueContainer: {
    backgroundColor: colors.success,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  valueText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  slider: {
    width: '100%',
    height: 40,
  },
  sliderThumb: {
    backgroundColor: colors.success,
    width: 20,
    height: 20,
  },
  chartContainer: {
    alignItems: 'center',
    marginBottom: 20,
  },
  chartLegend: {
    flexDirection: 'row',
    marginTop: 16,
    gap: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  legendText: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  resultsContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 20,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  resultItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  resultLabel: {
    fontSize: 14,
    color: colors.textSecondary,
  },
  resultValue: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  scheduleContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    marginHorizontal: 0,
    marginVertical: 20,
    width: '100%',
    alignSelf: 'stretch',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  scheduleTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 20,
    textAlign: 'left',
  },
  scheduleTable: {
    borderRadius: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.surface,
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  tableHeaderText: {
    flex: 1,
    fontSize: 12,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'left',
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableCellText: {
    flex: 1,
    fontSize: 12,
    color: colors.textSecondary,
    textAlign: 'left',
    paddingHorizontal: 4,
  },
  loadMoreButton: {
    backgroundColor: colors.success,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignSelf: 'center',
    marginTop: 20,
  },
  loadMoreText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  faqContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    marginHorizontal: 0,
    marginVertical: 20,
    width: '100%',
    alignSelf: 'stretch',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  faqHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 24,
  },
  faqTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    textAlign: 'left',
  },
  faqContent: {
    paddingHorizontal: 24,
    paddingBottom: 24,
  },
  faqItem: {
    marginBottom: 20,
  },
  faqQuestion: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  faqAnswer: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  infoContainer: {
    marginHorizontal: 0,
    marginVertical: 20,
    padding: 20,
    backgroundColor: colors.surface,
    borderRadius: 12,
    width: '100%',
    alignSelf: 'stretch',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  infoTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 16,
    textAlign: 'left',
  },
  infoSubtitle: {
    fontSize: 20,
    fontWeight: '600',
    color: colors.text,
    marginTop: 20,
    marginBottom: 12,
    textAlign: 'left',
  },
  infoText: {
    fontSize: 16,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 12,
    textAlign: 'left',
  },
  bulletList: {
    marginLeft: 16,
    marginBottom: 16,
  },
  bulletPoint: {
    fontSize: 16,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 4,
    textAlign: 'left',
  },
  formulaBox: {
    backgroundColor: colors.background,
    padding: 16,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
    marginVertical: 12,
  },
  formulaText: {
    fontSize: 16,
    fontFamily: 'monospace',
    color: colors.text,
    marginBottom: 4,
    textAlign: 'left',
  },
  stepsList: {
    marginLeft: 8,
    marginBottom: 16,
  },
  stepText: {
    fontSize: 16,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 8,
    textAlign: 'left',
  },
  // SEO Content Styles
  seoContainer: {
    backgroundColor: colors.cardBackground,
    borderRadius: 12,
    padding: 24,
    marginHorizontal: 0,
    marginVertical: 20,
    width: '100%',
    alignSelf: 'stretch',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  seoTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    color: colors.text,
    marginBottom: 24,
    textAlign: 'center',
  },
  seoSection: {
    marginBottom: 32,
  },
  seoSubtitle: {
    fontSize: 22,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 16,
    textAlign: 'left',
  },
  seoText: {
    fontSize: 16,
    color: colors.textSecondary,
    lineHeight: 24,
    marginBottom: 16,
    textAlign: 'left',
  },
  featureGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 16,
  },
  featureItem: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 16,
    width: isMobile ? '100%' : '48%',
    marginBottom: 16,
  },
  featureTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  featureText: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  comparisonTable: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    overflow: 'hidden',
    marginTop: 16,
  },
  comparisonRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  comparisonHeader: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: colors.text,
    padding: 12,
    backgroundColor: colors.primary,
    color: 'white',
    textAlign: 'center',
  },
  comparisonCell: {
    flex: 1,
    fontSize: 14,
    color: colors.textSecondary,
    padding: 12,
    textAlign: 'center',
  },
  stepsList: {
    marginTop: 16,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 16,
  },
  stepNumber: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.primary,
    backgroundColor: colors.background,
    borderRadius: 20,
    width: 36,
    height: 36,
    textAlign: 'center',
    lineHeight: 36,
    marginRight: 16,
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 4,
  },
  stepDescription: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  strategiesList: {
    marginTop: 16,
  },
  strategyItem: {
    backgroundColor: colors.surface,
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  strategyTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
    marginBottom: 8,
  },
  strategyText: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  mistakesList: {
    marginTop: 16,
  },
  mistakeItem: {
    backgroundColor: '#FEF2F2',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: colors.error,
  },
  mistakeTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.error,
    marginBottom: 8,
  },
  mistakeText: {
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
});

export default EMICalculatorScreen;